import React, { useMemo, useState } from 'react';
import { Button, Text, useToast, HStack, Box } from '@chakra-ui/react';
import { FaDownload, FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { getData } from '../../../utils/httpClient';
import AdminPageShell from '../../../components/layout/AdminPageShell';
import { primaryButtonProps, ghostButtonProps, adminColors } from '../../../theme/adminUi';
import ReportPeriodBar from './ReportPeriodBar';
import ReportPreviewPanel from './ReportPreviewPanel';
import {
  rangeLastMonths,
  validatePeriod,
  formatDateUy,
} from './reportUtils';

const PREVIEW_COLUMNS = [
  { key: 'customerName', label: 'Cliente' },
  { key: 'customerPhone', label: 'Contacto' },
  { key: 'vehicle', label: 'Vehículo' },
  { key: 'lastServiceName', label: 'Último servicio' },
  { key: 'lastServiceDate', label: 'Última visita' },
];

export default function InactiveCustomersReport() {
  const toast = useToast();
  const navigate = useNavigate();
  const defaults = useMemo(() => rangeLastMonths(12), []);
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasPreviewed, setHasPreviewed] = useState(false);

  const load = async () => {
    const error = validatePeriod(startDate, endDate);
    if (error) {
      toast({ title: 'Período inválido', description: error, status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    setLoading(true);
    try {
      const res = await getData(
        `/customers/reports/inactive?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
      );
      setRows(res?.payload?.items || []);
      setHasPreviewed(true);
    } catch {
      toast({ title: 'Error al cargar reporte', status: 'error', duration: 3000 });
      setRows([]);
      setHasPreviewed(true);
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = () => {
    if (!rows.length) {
      toast({
        title: 'Sin datos',
        description: 'Generá la vista previa antes de descargar.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    const data = rows.map((r) => ({
      Cliente: r.customerName,
      Documento: r.customerDocument || '',
      Teléfono: r.customerPhone || '',
      Patente: r.vehiclePlate || '',
      Marca: r.vehicleBrand || '',
      Modelo: r.vehicleModel || '',
      'Último servicio': r.lastServiceName || '',
      'Fecha último servicio': r.lastServiceDate
        ? formatDateUy(r.lastServiceDate)
        : 'Sin servicio',
      'Meses sin visita': r.monthsSinceVisit ?? '',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), 'Clientes inactivos');
    XLSX.writeFile(wb, `clientes_inactivos_${startDate}_${endDate}.xlsx`);
  };

  return (
    <AdminPageShell
      title="Clientes sin visita"
      actions={
        <HStack spacing={2}>
          <Button
            {...ghostButtonProps}
            leftIcon={<FaArrowLeft />}
            onClick={() => navigate('/admin/reports')}
          >
            Volver
          </Button>
          <Button
            {...primaryButtonProps}
            leftIcon={<FaDownload />}
            onClick={exportExcel}
            isDisabled={!rows.length}
          >
            Descargar Excel
          </Button>
        </HStack>
      }
    >
      <ReportPeriodBar
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onApply={load}
        loading={loading}
        description="Clientes/vehículos que no tuvieron servicio (excepto garantía) dentro del período elegido."
      />

      <ReportPreviewPanel
        title="Vista previa"
        count={rows.length}
        countLabel={`${rows.length} registros`}
        loading={loading}
        emptyMessage={
          hasPreviewed
            ? 'No hay clientes inactivos en este período.'
            : 'Seleccioná un período y generá la vista previa.'
        }
        emptyHint="Por defecto se propone el último año; podés ajustar las fechas libremente."
        columns={PREVIEW_COLUMNS}
        rows={rows}
        getRowKey={(r) => `${r.customerId}-${r.vehicleId}`}
        renderCell={(r, col) => {
          if (col.key === 'customerName') {
            return (
              <Box>
                <Text fontWeight="semibold" fontSize="sm">
                  {r.customerName}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {r.customerDocument || '—'}
                </Text>
              </Box>
            );
          }
          if (col.key === 'vehicle') {
            return (
              <Box>
                <Text fontWeight="semibold" fontSize="sm">
                  {r.vehiclePlate || '—'}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {[r.vehicleBrand, r.vehicleModel].filter(Boolean).join(' ') || '—'}
                </Text>
              </Box>
            );
          }
          if (col.key === 'lastServiceDate') {
            return (
              <Box>
                <Text fontSize="sm" whiteSpace="nowrap">
                  {r.lastServiceDate ? formatDateUy(r.lastServiceDate) : 'Sin registro'}
                </Text>
                {r.monthsSinceVisit != null && (
                  <Text fontSize="xs" color={adminColors.primary}>
                    {r.monthsSinceVisit} meses
                  </Text>
                )}
              </Box>
            );
          }
          return (
            <Text fontSize="sm" color={r[col.key] ? 'inherit' : 'gray.400'}>
              {r[col.key] || '—'}
            </Text>
          );
        }}
      />
    </AdminPageShell>
  );
}
